import Box from '@mui/material/Box';
import { Collapsible } from '@openx/components-core/lib/Collapsible/Collapsible.tsx';
import { type SegmentStatus, segmentStatus } from '@openx/types/segments.ts';
import { type JSX, useMemo, useState } from 'react';
import type { AudienceListType, SegmentTreeNode } from '../../../../../../../modules/audiences/AudienceForm/types.ts';
import { daysToExpiration } from '../../../../../utils/daysToExpiration.ts';
import { isSoonToBeExpired } from '../../../../../utils/isSoonToBeExpired.ts';
import { LeafNode } from './LeafNode.tsx';
import { ParentNodeTitle } from './ParentNodeTitle.tsx';

type BaseTreeNodeProps = {
  node: SegmentTreeNode;
  path: string[];
  type: AudienceListType;
};

type TreeNodeProps = BaseTreeNodeProps &
  ({ readOnly: true } | { readOnly?: false; onLeafDelete: (node: SegmentTreeNode) => void });

export const TreeNode = ({ node, path, type, ...rest }: TreeNodeProps): JSX.Element => {
  const [expanded, setExpanded] = useState(false);
  const children = node.children ?? [];
  const titleText = path.join(' ― ');

  const hasArchivedChild = useMemo(() => children.some(child => child.is_archived), [children]);
  const hasExpiredChild = useMemo(() => children.some(child => child.status === segmentStatus.EXPIRED), [children]);

  const alertProps = useMemo(() => {
    const status = node.status as SegmentStatus;
    const isExpired = status === segmentStatus.EXPIRED;
    const isExpiresSoon = isSoonToBeExpired(node?.expiration_date || '', status);
    const days = daysToExpiration(node?.expiration_date);

    return {
      showArchived: node.is_archived || (!expanded && hasArchivedChild),
      showExpired: isExpired || (!expanded && hasExpiredChild),
      showExpiresSoon: isExpiresSoon,
      daysRemaining: isExpiresSoon ? (days ?? undefined) : undefined,
    };
  }, [node, expanded, hasArchivedChild, hasExpiredChild]);

  return (
    <Collapsible
      titleRender={<ParentNodeTitle namePath={titleText} type={type} description={node.description} {...alertProps} />}
      expanded={expanded}
      onExpandToggle={setExpanded}
      disableMargin
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ml: 3, mt: 0.5 }}>
        {children.map(child => (
          <LeafNode key={child.id} node={child} type={type} {...rest} />
        ))}
      </Box>
    </Collapsible>
  );
};
